import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const CATEGORIES = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
];

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  const reset = () => {
    setMessage("");
    setCategory("idea");
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    reset();
  };

  const submit = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error("Please add a short message");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/feedback", {
        message: trimmed,
        category,
        page_url: `${location.pathname}${location.search}`,
      });
      track("feedback_submitted", { category, page: location.pathname });
      toast.success("Thanks! Your feedback was sent.");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        data-testid="feedback-open-button"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-5 right-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#4A6148] text-white text-sm font-medium shadow-lg hover:bg-[#3B5B3E] transition-colors"
        aria-label="Send feedback"
      >
        <MessageSquarePlus size={16} strokeWidth={2} />
        <span className="hidden sm:inline">Send feedback</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#2C302E]/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={close}
          data-testid="feedback-modal-backdrop"
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-heading font-semibold text-lg text-[#2C302E]">Send feedback</div>
                <div className="text-xs text-[#8A918D]">Bug, idea, or anything else — thanks for helping.</div>
              </div>
              <button
                onClick={close}
                className="btn-ghost !p-2"
                aria-label="Close"
                data-testid="feedback-close-button"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-xs font-medium text-[#59605C] mb-1.5 uppercase tracking-wide">Category</div>
                <div className="flex gap-2" data-testid="feedback-category-group">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      data-testid={`feedback-category-${c.value}`}
                      onClick={() => setCategory(c.value)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        category === c.value
                          ? "bg-[#EEF3EC] border-[#3B5B3E] text-[#3B5B3E]"
                          : "bg-white border-[#EBE6E0] text-[#59605C] hover:bg-[#F2EFEB]"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="feedback-message" className="text-xs font-medium text-[#59605C] mb-1.5 uppercase tracking-wide block">
                  Message
                </label>
                <textarea
                  id="feedback-message"
                  data-testid="feedback-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  maxLength={4000}
                  placeholder="What did you notice, or what would you like to see?"
                  className="w-full rounded-2xl border border-[#D8CFB9] bg-white px-3 py-2.5 text-sm text-[#2C302E] focus:outline-none focus:ring-2 focus:ring-[#4A6148]/30 focus:border-[#4A6148] resize-y"
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={close}
                  disabled={submitting}
                  className="btn-ghost text-sm"
                  data-testid="feedback-cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={submitting || !message.trim()}
                  className="btn-primary text-sm disabled:opacity-60"
                  data-testid="feedback-submit-button"
                >
                  {submitting ? "Sending…" : "Send feedback"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
